import React, { useState } from 'react';
import { useCMS } from '../context/CMSContext';
import SEO from '../components/SEO';

export default function Contact() {
  const { getPage } = useCMS();
  const page = getPage('kontakt');

  // Formular tilstande
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot felt
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  if (!page) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="h-8 w-48 bg-brand-sand/40 rounded" />
          <div className="h-4 w-64 bg-brand-sand/30 rounded" />
        </div>
      </div>
    );
  }

  const { title, content, meta_title, meta_description } = page;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Der skete en fejl ved afsendelsen af formularen. Prøv venligst igen.');
      }

      setStatus({
        submitting: false,
        success: true,
        error: null,
      });

      // Nulstil formular
      setFormData({
        name: '',
        phone: '',
        email: '',
        description: '',
        website_url: '',
      });
    } catch (err) {
      console.error('Fejl ved afsendelse af kontaktformular:', err);
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der skete en uventet fejl. Prøv venligst igen senere.',
      });
    }
  };

  return (
    <>
      <SEO 
        title={title?.da || 'Kontakt'} 
        description={meta_description?.da} 
      />

      {/* Header Sektion */}
      <section className="bg-brand-beige/30 border-b border-brand-sand/30 py-12 md:py-16">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-brown mb-3 block">
            Kontakt Os
          </span>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-brand-dark">
            {title?.da}
          </h1>
          <p className="text-brand-brown text-sm md:text-base mt-4 max-w-xl mx-auto">
            Har du spørgsmål, eller ønsker du at booke en tid til behandling? Ring, skriv en mail eller udfyld formularen nedenfor.
          </p>
        </div>
      </section>

      {/* Hovedindhold */}
      <section className="py-16 md:py-24 bg-brand-cream">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Venstre side: Kontaktoplysninger */}
            <div className="lg:col-span-5 flex flex-col gap-8">
              <div className="p-8 rounded-brand bg-brand-beige/30 border border-brand-sand/40 flex flex-col gap-6">
                <h3 className="font-serif text-xl font-bold text-brand-dark">
                  Vores oplysninger
                </h3>
                
                <div className="flex flex-col gap-5 text-sm text-brand-brown">
                  {/* Adresse */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-brand bg-brand-cream border border-brand-sand flex items-center justify-center text-brand-dark shrink-0">
                      <i className="fa-regular fa-location-dot" />
                    </div>
                    <div>
                      <span className="font-semibold text-brand-dark block mb-0.5">Adresse</span>
                      <span className="leading-relaxed">
                        Borupvej 30, Snekkerup,<br />
                        4140 Borup
                      </span>
                    </div>
                  </div>

                  {/* Telefon */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-brand bg-brand-cream border border-brand-sand/40 flex items-center justify-center text-brand-dark shrink-0">
                      <i className="fa-regular fa-phone" />
                    </div>
                    <div>
                      <span className="font-semibold text-brand-dark block mb-0.5">Telefon</span>
                      <a 
                        href="tel:+4526992415" 
                        className="hover:text-brand-dark transition-colors duration-200 whitespace-nowrap font-medium"
                      >
                        26 99 24 15
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-brand bg-brand-cream border border-brand-sand/40 flex items-center justify-center text-brand-dark shrink-0">
                      <i className="fa-regular fa-envelope" />
                    </div>
                    <div>
                      <span className="font-semibold text-brand-dark block mb-0.5">Email</span>
                      <a 
                        href="mailto:kontakt@equigramvet.dk" 
                        className="hover:text-brand-dark transition-colors duration-200 whitespace-nowrap font-medium"
                      >
                        kontakt@equigramvet.dk
                      </a>
                    </div>
                  </div>

                  {/* CVR */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-brand bg-brand-cream border border-brand-sand/40 flex items-center justify-center text-brand-dark shrink-0">
                      <i className="fa-regular fa-id-card" />
                    </div>
                    <div>
                      <span className="font-semibold text-brand-dark block mb-0.5">CVR-nummer</span>
                      <span className="font-medium">46305892</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Praktisk info boks */}
              <div className="p-6 rounded-brand border border-brand-sand bg-brand-beige/40">
                <h4 className="font-serif text-base font-bold text-brand-dark mb-2 flex items-center gap-2">
                  <i className="fa-regular fa-van-shuttle text-brand-brown" /> Kørsel til behandlinger
                </h4>
                <p className="text-brand-brown text-xs leading-relaxed">
                  Vi er fuldt mobile og kører ud til stalde og adresser i hele Sjælland efter aftale. Specialaftaler for resten af Danmark kan også laves efter ønske. Vi planlægger ruterne så optimalt som muligt.
                </p>
              </div>
            </div>

            {/* Højre side: Kontaktformular */}
            <div className="lg:col-span-7">
              <div className="p-8 rounded-brand bg-brand-cream border border-brand-sand/50 shadow-sm">
                <h3 className="font-serif text-2xl font-bold text-brand-dark mb-6">
                  Send os en besked
                </h3>

                {status.success ? (
                  <div className="p-6 rounded-brand bg-brand-beige border border-brand-sand/60 text-center flex flex-col items-center gap-4 animate-in fade-in duration-300">
                    <div className="w-12 h-12 rounded-full bg-brand-dark text-brand-cream flex items-center justify-center text-xl">
                      <i className="fa-regular fa-check" />
                    </div>
                    <div>
                      <h4 className="font-serif text-lg font-bold text-brand-dark mb-1">Tak for din henvendelse!</h4>
                      <p className="text-brand-brown text-sm">Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.</p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                    
                    {/* Antispam Honeypot Felt (ABSOLUT SKJULT) */}
                    <div className="sr-only absolute -z-50 overflow-hidden h-0 w-0">
                      <label htmlFor="website_url">Efterlad dette felt tomt</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    {/* Navn */}
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="name" className="text-xs font-semibold text-brand-dark">
                        Navn <span className="text-brand-brown/70">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Dit fulde navn"
                        className="w-full px-4 py-3 rounded-brand border border-brand-sand bg-brand-cream text-brand-dark text-sm focus:outline-none focus:border-brand-brown focus:ring-1 focus:ring-brand-brown transition-all duration-200"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {/* Telefonnummer */}
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="phone" className="text-xs font-semibold text-brand-dark">
                          Telefonnummer <span className="text-brand-brown/70">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="F.eks. 26992415"
                          className="w-full px-4 py-3 rounded-brand border border-brand-sand bg-brand-cream text-brand-dark text-sm focus:outline-none focus:border-brand-brown focus:ring-1 focus:ring-brand-brown transition-all duration-200"
                        />
                      </div>

                      {/* Email */}
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="email" className="text-xs font-semibold text-brand-dark">
                          E-mailadresse <span className="text-brand-brown/70">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="F.eks. navn@mail.dk"
                          className="w-full px-4 py-3 rounded-brand border border-brand-sand bg-brand-cream text-brand-dark text-sm focus:outline-none focus:border-brand-brown focus:ring-1 focus:ring-brand-brown transition-all duration-200"
                        />
                      </div>
                    </div>

                    {/* Beskrivelse */}
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="description" className="text-xs font-semibold text-brand-dark">
                        Beskrivelse af henvendelse <span className="text-brand-brown/70">*</span>
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        required
                        rows="5"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Beskriv venligst dit dyrs symptomer, race, alder, og hvad du ønsker hjælp til..."
                        className="w-full px-4 py-3 rounded-brand border border-brand-sand bg-brand-cream text-brand-dark text-sm focus:outline-none focus:border-brand-brown focus:ring-1 focus:ring-brand-brown transition-all duration-200 resize-none"
                      />
                    </div>

                    {/* Fejlbesked */}
                    {status.error && (
                      <div className="p-4 rounded bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-2">
                        <i className="fa-regular fa-circle-exclamation shrink-0" />
                        <span>{status.error}</span>
                      </div>
                    )}

                    {/* Indsendelsesknap */}
                    <button
                      type="submit"
                      disabled={status.submitting}
                      className="w-full py-3.5 bg-brand-dark text-brand-cream font-medium rounded-brand hover:bg-brand-brown transition-colors duration-200 shadow-md flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer focus:outline-none"
                    >
                      {status.submitting ? (
                        <>
                          <i className="fa-regular fa-spinner-third animate-spin" />
                          Sender besked...
                        </>
                      ) : (
                        <>
                          <i className="fa-regular fa-paper-plane" />
                          Send besked
                        </>
                      )}
                    </button>

                  </form>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
